/**
 * Utility functions for Access Order security logic
 * These functions help prepare user information for category filtering
 */

const clampAccessValue = (value) => Math.min(10, Math.max(0, value));

export const normalizeAccessRange = (raw) => {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const parsedMin = parseInt(raw.min, 10);
    const parsedMax = parseInt(raw.max, 10);
    const safeMin = Number.isFinite(parsedMin) ? parsedMin : 0;
    const safeMax = Number.isFinite(parsedMax) ? parsedMax : safeMin;
    const clampedMin = clampAccessValue(safeMin);
    const clampedMax = clampAccessValue(safeMax);
    if (clampedMin > clampedMax) {
      return { min: clampedMax, max: clampedMin };
    }
    return { min: clampedMin, max: clampedMax };
  }

  const parsedLegacy = parseInt(raw, 10);
  const legacyMax = Number.isFinite(parsedLegacy) ? parsedLegacy : 0;
  return { min: 0, max: clampAccessValue(legacyMax) };
};

/**
 * Extracts the user's access range from employee_permission details
 * @param {Array} employeePermissions - Array of employee permissions from state.employee_permission.employee_permission
 * @returns {{min: number, max: number, hasPermission: boolean, isLegacy: boolean}} The access range with metadata
 */
export const getAccessRangeFromPermissions = (employeePermissions) => {
  if (!employeePermissions || !Array.isArray(employeePermissions)) {
    return { min: 0, max: 0, hasPermission: false, isLegacy: false };
  }

  // Look through permission records to find special_permission
  for (const permission of employeePermissions) {
    // Check both auth_details and details arrays for special_permission
    const detailsArrays = [
      permission.auth_details,
      permission.details,
      permission.updated?.auth_details,
      permission.updated?.details,
      permission.created?.auth_details,
      permission.created?.details,
    ].filter(Boolean);

    for (const detailsArray of detailsArrays) {
      if (Array.isArray(detailsArray)) {
        const specialPermission = detailsArray.find(
          (detail) => 
            detail?.name_id === "special_permission" ||
            detail?.name === "special_permission" ||
            detail?.value === "special_permission"
        );

        if (specialPermission) {
          // Get the raw response or value
          const rawRange =
            specialPermission.response !== undefined
              ? specialPermission.response
              : specialPermission.value;

          // Detect if it's legacy format (number) or new format (object)
          // New format: object with min/max properties (not null, not array)
          // Legacy format: number or string that can be parsed as number
          const isLegacy = !(rawRange && typeof rawRange === "object" && !Array.isArray(rawRange) &&
            typeof rawRange.min !== "undefined" && typeof rawRange.max !== "undefined");

          // Normalize the range
          const normalized = normalizeAccessRange(rawRange);

          return {
            ...normalized,
            hasPermission: true,
            isLegacy: isLegacy,
          };
        }
      }
    }
  }

  return { min: 0, max: 0, hasPermission: false, isLegacy: false };
};

/**
 * Prepares user info object for SET_CORE_DATA action
 * @param {number} accountType - User's account type from auth
 * @param {Array} employeePermissions - Employee permissions array
 * @returns {Object} User info object with account_type and access_range
 */
export const prepareUserInfoForCoreData = (accountType, employeePermissions) => {
  return {
    account_type: accountType,
    access_range: getAccessRangeFromPermissions(employeePermissions),
  };
};

/**
 * Filters categories based on Access Order security logic
 * This is a standalone version that can be used outside the reducer if needed
 * @param {Array} categories - The categories array to filter
 * @param {number} accountType - User's account type
 * @param {{min: number, max: number, hasPermission: boolean, isLegacy: boolean}} accessRange - User's access range from special_permission
 * @returns {Array} Filtered categories
 */
export const filterCategoriesByAccessOrder = (
  categories,
  accountType,
  accessRange
) => {
  // Admin users (account_type === 5) see all categories
  if (accountType === 5) {
    return categories;
  }

  // Default to no permission if accessRange is not provided or invalid
  const hasPermission = accessRange?.hasPermission === true;
  const isLegacy = accessRange?.isLegacy === true;
  const min = accessRange?.min ?? 0;
  const max = accessRange?.max ?? 0;

  // For non-admins, filter based on permission state
  return categories.filter((category) => {
    // Case 1: No permission - only show categories without access levels
    if (!hasPermission) {
      return !category.require_special_access;
    }

    // Case 2: Legacy format - show categories without access levels OR categories <= max
    if (isLegacy) {
      if (!category.require_special_access) {
        return true;
      }
      return category.access_order <= max;
    }

    // Case 3: New range format - show categories within range
    // If min === 0, also include categories without access levels
    if (!category.require_special_access) {
      return min === 0;
    }
    return (
      category.access_order >= min &&
      category.access_order <= max
    );
  });
};

